import React, { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { downloadRespondentsCsv, errorMessage, getProject, getProjectStatistics } from '../../api';
import { paths } from '../../app/paths';
import { useAsyncData } from '../../hooks/useAsyncData';
import { saveBlob } from '../../lib/saveBlob';
import type { Project, ProjectStatistics, QuotaSettings } from '../../types';
import { Button, Card, CardBody, EmptyState, ErrorState, InlineAlert, Skeleton, StatusBadge, TextField, useToast } from '../../ui';
import { CampaignProgressCard } from '../projects/CampaignProgressCard';
import { CellFill } from '../wizard/CellFill';
import { browserTimeZone, datesOutOfOrder, funnelOf } from './reportsModel';
import { num, pct } from './metrics';
import { TrendCard } from './TrendCard';

type QuotaRowData = NonNullable<QuotaSettings['data']>[number];

/** A short name for a quota row ("18–30", "18–30 · by region"), from the target group's saved quotas. */
function describeRow(row: QuotaRowData | undefined, index: number): string {
  if (!row) return `Row ${index + 1}`;
  const age = row.age ? `${row.age.minAge}–${row.age.maxAge}` : 'All ages';
  const by = [row.stateIds && row.stateIds.length ? 'regions' : '', row.profilingAnswerIds && row.profilingAnswerIds.length ? 'profiling' : ''].filter(Boolean);
  return `Row ${index + 1}: ${age}${by.length ? ` · ${by.join(' + ')}` : ''}`;
}

/**
 * One campaign's report: how its respondents ended (a bar per status against everyone who entered), the headline figures and
 * the per target group lines (the progress card), how full each quota row is, and the respondents as a CSV.
 * Everything is the server's own figures from GET /project/{id}/statistics; nothing is calculated here except the share of
 * the total shown beside each bar. Before respondents use the entry link, the figures come only from the survey redirects.
 */
export function CampaignReportPage() {
  const { projectId } = useParams();
  const { notify } = useToast();
  const project = useAsyncData<Project | null>((signal) => getProject(projectId, { signal }), [projectId]);
  const stats = useAsyncData<ProjectStatistics | null>((signal) => getProjectStatistics(projectId, { signal }), [projectId]);
  // The days respondents entered: they limit the trend and the respondents CSV (the figures above are all-time; the statistics have no dates).
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const tz = browserTimeZone();
  const outOfOrder = datesOutOfOrder(from, to);
  const [exporting, setExporting] = useState(false);
  const exportingRef = useRef(false);

  const exportCsv = async () => {
    if (exportingRef.current || !project.data || outOfOrder) return;
    exportingRef.current = true;
    setExporting(true);
    try {
      const { blob, filename } = await downloadRespondentsCsv(project.data.id, { from, to, tz });
      saveBlob(blob, filename || `respondents-${project.data.id}.csv`); // the server's name when it sends one
    } catch (err) {
      notify(errorMessage(err), { tone: 'danger', duration: 7000 });
    } finally {
      exportingRef.current = false;
      setExporting(false);
    }
  };

  if (project.loading && !project.data) return <div className="mx-auto max-w-6xl space-y-4" aria-busy="true"><Skeleton className="h-8 w-72" /><Skeleton className="h-40 w-full" /></div>;
  if (project.error) return <Card className="mx-auto max-w-6xl"><ErrorState title="Could not load this campaign" message={errorMessage(project.error)} onRetry={project.reload} /></Card>;
  if (!project.data) {
    return (
      <Card className="mx-auto max-w-6xl">
        <EmptyState title="Campaign not found" description="It may have been deleted, or the link is wrong." action={<Link to={paths.reports()} className="text-sm font-semibold text-brand-700 hover:underline">Back to reports</Link>} />
      </Card>
    );
  }

  const campaign = project.data;
  const s = stats.data;
  const funnel = s ? funnelOf(s) : null;
  const groups = (s && s.targetGroups) || [];
  const quotaRowsOf = (targetGroupId: string) => {
    const saved = (campaign.targetGroups || []).find((g) => g.id === targetGroupId);
    return (saved && saved.quotaSettings && saved.quotaSettings.data) || [];
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <nav aria-label="Breadcrumb" className="text-xs text-muted">
        <Link to={paths.reports()} className="hover:underline">Reports</Link> <span aria-hidden="true">›</span> <span className="text-ink">{campaign.name}</span>
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold tracking-tight text-ink">{campaign.name} <StatusBadge status={campaign.projectStatus} /></h1>
          <p className="mt-1 text-sm text-muted">{s ? (s.source === 'sessions' ? 'Based on respondent sessions.' : 'Based on the survey redirect links only. Respondent figures appear once panelists use the entry link.') : 'Report'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={exportCsv} loading={exporting} disabled={outOfOrder}>Download respondents CSV</Button>
          <Link to={paths.project(campaign.id)} className="text-sm font-semibold text-brand-700 hover:underline">Open campaign</Link>
        </div>
      </div>

      <Card>
        <CardBody>
          <div className="flex flex-wrap items-end gap-3">
            <TextField label="Entered from" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            <TextField label="Entered to" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} error={outOfOrder ? 'Not before the first day' : undefined} />
            {(from || to) && <Button variant="ghost" size="sm" onClick={() => { setFrom(''); setTo(''); }}>Clear dates</Button>}
          </div>
          <p className="mt-2 text-xs text-muted">The dates limit the trend and the respondents CSV ({tz}). The figures below cover the whole campaign.</p>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="text-base font-semibold text-ink">How respondents ended</h2>
          {stats.error ? (
            <div className="mt-4"><InlineAlert tone="warn">Could not load the figures: {errorMessage(stats.error)} <button type="button" onClick={stats.reload} className="font-semibold underline">Try again</button></InlineAlert></div>
          ) : !s || !funnel ? (
            stats.loading ? <div className="mt-4 space-y-2" aria-busy="true"><Skeleton className="h-6 w-full" /><Skeleton className="h-6 w-full" /></div> : <div className="mt-4"><InlineAlert tone="info">No statistics are available for this campaign yet.</InlineAlert></div>
          ) : funnel.base === 0 ? (
            <div className="mt-4"><InlineAlert tone="info">No respondents yet.</InlineAlert></div>
          ) : (
            <>
              <p className="mt-1 text-sm text-muted"><b className="text-ink">{num(funnel.base)}</b> {funnel.baseLabel}.</p>
              <ul className="mt-4 space-y-2.5" aria-label="Respondent outcomes">
                {funnel.steps.map((step) => {
                  const share = funnel.base > 0 ? (step.value / funnel.base) * 100 : 0;
                  return (
                    <li key={step.key} className="grid grid-cols-[9rem_1fr_7rem] items-center gap-3 text-sm">
                      <span className="text-muted">{step.label}</span>
                      <span className="h-3 overflow-hidden rounded-full bg-chip" role="img" aria-label={`${step.label}: ${step.value} of ${funnel.base}`}>
                        <span className="block h-full rounded-full bg-brand-600" style={{ width: `${Math.max(0, Math.min(100, share))}%` }} />
                      </span>
                      <span className="text-right tabular-nums text-ink">{num(step.value)} <span className="text-xs text-muted">({pct(share)})</span></span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </CardBody>
      </Card>

      <CampaignProgressCard key={campaign.id} project={campaign} />

      {!outOfOrder && <TrendCard projectId={campaign.id} groups={(campaign.targetGroups || []).map((g, i) => ({ id: g.id, name: g.name || `Target group ${i + 1}` }))} from={from} to={to} tz={tz} />}

      {groups.some((g) => g.cells && g.cells.length > 0) && (
        <Card>
          <CardBody>
            <h2 className="text-base font-semibold text-ink">Quota fill</h2>
            <p className="mt-1 text-sm text-muted">Completes so far against each quota row, from respondents. A row is full when the server says no place is left.</p>
            <div className="mt-4 space-y-5">
              {groups.filter((g) => g.cells && g.cells.length > 0).map((g, gi) => (
                <section key={g.targetGroupId} aria-label={`Quota fill, ${g.name || `target group ${gi + 1}`}`}>
                  <h3 className="text-sm font-semibold text-ink">{g.name || `Target group ${gi + 1}`}</h3>
                  <ul className="mt-2 divide-y divide-line/70">
                    {(g.cells || []).map((cell) => (
                      <li key={cell.index} className="flex items-center justify-between gap-3 py-2 text-sm">
                        <span className="text-muted">{describeRow(quotaRowsOf(g.targetGroupId)[cell.index], cell.index)}</span>
                        <CellFill cell={cell} />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
